import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { PCL_CARD_EXPIRY_LIMIT_IN_MONTHS } from '../../utils/credit-card'
import { Button, Checkbox, Divider, Heading, Message, Text, useIsBreakpointLargeAndUp, useModal } from '@afterpaytouch/core'
import {
  ApplePayStatus,
  ApplePayError,
  CheckoutStatus,
  isCardScanRequiredErrorCode,
  NotificationError,
  PaymentMethodType,
  RecoverableErrorCode,
  PaymentSourceCaptureCardTypes,
  AutopayConfirmationModalKey,
  PayMonthlyTILAModalKey,
  Features,
} from '../../model'
import {
  ConsumerLendingPaymentSchedule,
  ConsumerLendingSummaryBreakdown,
  DisabledModal,
  Head,
  InlinePaymentMethod,
  NoErrorStatusGuard,
  OrderSummary,
  ShippingSummary,
  StartedStatusGuard,
  useSummaryNotification,
} from '../../component'
import { PaymentOptionsModal } from '../../component/PaymentOptionsModal'
import { create } from 'react-modal-promise'
import { Country, ErrorCode } from '@afterpaytouch/portal-api'
import { Route } from '../../config/router'
import { useCheckoutSizes } from '../../utils/responsive'
import { useTranslation } from '../../utils/convergence'
import {
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useCountry,
  useEnforcePclCardExpiryLimit,
  useIsDeferredShipping,
  useIsExpress,
  useIsPickup,
  useIsReadyToConfirm,
  usePreferredBankAccount,
  usePreferredCard,
  usePreferredPaymentMethod,
  useSelectedPaymentMethod,
  useApplePayState,
  useApplePayFeature,
  resetCheckoutError,
  setConfirmTermsViewed,
  useCachedFeatureFlag,
} from '../../state'
import styles from '../summary-pay-monthly/style.module.css'
import redesignStyles from './style.module.css'
import { AutopayButton, AutopayModal } from '../../component/AutopayToggle'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { NonTerminalErrors, TrackingEvent } from '../../model/monitoring'
import { useGetIsAutoPayAgreed } from '../../component/AutopayToggle/hooks'
import { Terms as TILATerms } from '../../component/TruthInLending/Terms'
import { TruthInLendingModal } from '../../component/TruthInLending/TruthInLendingModal'
import { TruthInLendingButton } from '../../component/TruthInLending/TruthInLendingButton'
import { isPayMonthlyConfirmationBlocked } from '../summary-pay-monthly/isPayMonthlyConfirmationBlocked'

/**
 * Tags the PM summary's page-view telemetry with the page structure version (kept after the
 * legacy-vs-redesign A/B concluded so existing Amplitude queries keep working). Bump the marker
 * if the structure changes materially.
 */
export const PAY_MONTHLY_SUMMARY_VERSION_REDESIGN = 'pm redesign v1'

// a11y (CASHDAP-3087): id linking the TILA consent error message to the checkbox via
// aria-describedby, and given role="alert" so it's announced on submit failure (not colour-only).
export const TILA_TERMS_ERROR_ID = 'tila-terms-checkbox-error'

/**
 * The Pay Monthly (consumer-lending) summary page (project: Dynamic checkout unification 2026).
 * Built as the redesign arm of the `checkout-pay-monthly-summary-redesign` A/B; the experiment
 * concluded and the legacy PM summary was removed (CASHDAP-3350), making this the only PM summary.
 * Routed from `page/summary/Summary.tsx` for consumer-lending orders (Apple Retail excepted).
 *
 * Uses the canonical Bronte status guards — `NoErrorStatusGuard` (renders nothing on
 * `CheckoutStatus.Error`) wrapping `StartedStatusGuard` (body only once the checkout has started),
 * matching `page/summary-bronte/BronteSummary.tsx`.
 *
 * `SummaryRouteGuard` (the third Bronte shell guard) is intentionally NOT adopted: it routes
 * `IdentityIneligible` via `getIdentityVerificationRoute`, which falls through to
 * `Route.TERMINAL_ERROR` for PM's payload (no identityResponse / errorCode), whereas PM redirects
 * to the verify flow. PM's bespoke routing effect is kept until that parity is confirmed.
 *
 * PM-specific slots: term length + installment dates, TILA checkbox + modal, autopay (US), and the
 * CL-specific Amplitude events.
 */
export const PayMonthlySummaryRedesignedPage: FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const { status, error } = useCheckoutState()
  const applePay = useApplePayState()
  const { sectionHeadingSize, headingSize, textSize, smallTextSize } = useCheckoutSizes()
  const ready = useIsReadyToConfirm()
  const country = useCountry()
  const requiresPayMonthlyAutopayControls = country === Country.US
  const requiresPayMonthlyTermsConsent = country !== Country.AU
  const [cardExpiryMonthsEventSent, setCardExpiryMonthsEventSent] = useState<boolean>(false)
  const [notAcceptedPaymentMethodEventSent, setnotAcceptedPaymentMethodEventSent] = useState<boolean>(false)
  const [clicked, setClicked] = useState<boolean>(false)
  const [notification, setNotification] = useState<NotificationError>()
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const { account, valid: bankAccountValid, reason: bankAccountReason } = usePreferredBankAccount() ?? {}
  const { card, valid, reason, switched } = usePreferredCard() ?? {}
  const selectedPaymentMethod = useSelectedPaymentMethod()
  // InlinePaymentMethod's `SummaryNotification` publishes what it is actually showing through
  // `SummaryNotificationContext` (provided by page/summary/Summary.tsx). Read that instead of
  // predicting its branches from cohort/selection state: the prediction suppressed the page-level
  // fallback whenever `bankAccountReason` was set, even though the inline component shows nothing
  // for a CL order switched off a checking account — hiding the "switched to your debit card"
  // notice entirely (nightly build 4086, regression from CASHDAP-3350).
  const { notification: inlineNotification } = useSummaryNotification()
  useEnforcePclCardExpiryLimit()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const isDesktop = useIsBreakpointLargeAndUp()
  const isPickup = useIsPickup()
  const isExpress = useIsExpress()
  const isDeferredShipping = useIsDeferredShipping()
  const cardExpiryLimitInMonths = PCL_CARD_EXPIRY_LIMIT_IN_MONTHS
  const applePayFeature = useApplePayFeature()
  const cashPayMonthlyEnabled = useCachedFeatureFlag(Features.CONVERGENCE_CASH_PAY_MONTHLY_ENABLED, false)
  const { showModal: showAutopayConfirmationModal } = useModal(AutopayConfirmationModalKey)
  const autoPayAgreed = useGetIsAutoPayAgreed() ?? false
  const requiresAutopayConfirmation = requiresPayMonthlyAutopayControls && autoPayAgreed
  const [documentId, setDocumentId] = useState<string>('')
  const [tilDisclosure, setTilDisclosure] = useState<boolean>(false)
  const [isTILATermsCheckboxChecked, setTILATermsCheckboxChecked] = useState<boolean>(false)
  const [showTILACheckboxErrorMessage, setShowTILACheckboxErrorMessage] = useState<boolean>(false)
  const { showModal: showPayMonthlyTILAModal } = useModal(PayMonthlyTILAModalKey)
  const showPaymentOptionsModal = create(PaymentOptionsModal)
  const hasBlockingPaymentError = isPayMonthlyConfirmationBlocked({
    applePayEnabledForConsumerLending: applePayFeature.enabledForConsumerLending,
    applePayError: applePay.error,
    bankAccountReason,
    bankAccountValid,
    cardReason: reason,
    cardValid: valid,
    cashPayMonthlyEnabled,
    hasBankAccount: account != null,
    hasCard: card != null,
    preferredPaymentMethod,
    selectedPaymentMethodType: selectedPaymentMethod?.type,
  })

  const setDocumentIdToShow = (documentId: string, isTilDisclosure: boolean = false): void => {
    // documentId can be undefined at runtime (e.g. an order with no creditScoreDisclosureDocumentId).
    // Don't open the modal for a missing doc: the lazy query data persists across links, so it would
    // otherwise show a blank iframe or the previously opened document (wrong legal disclosure). The
    // link simply no-ops instead.
    if ((documentId?.trim().length ?? 0) === 0) {
      return
    }
    showPayMonthlyTILAModal()
    setTilDisclosure(isTilDisclosure)
    setDocumentId(documentId)
  }

  const handleTILATermsCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const isChecked = e.target.checked
    setTILATermsCheckboxChecked(isChecked)
    if (isChecked) {
      setShowTILACheckboxErrorMessage(false)
    }
  }

  const renderTILACheckboxErrorMessage = (): ReactNode | null => {
    if (!requiresPayMonthlyTermsConsent || !showTILACheckboxErrorMessage) {
      return null
    }

    return (
      <div className={styles.tilaCheckboxErrorMessageContainer}>
        <Text color='Fire' size={smallTextSize} id={TILA_TERMS_ERROR_ID} role='alert'>
          {t('consumerLending:til.acceptToContinue')}
        </Text>
      </div>
    )
  }

  const handleButtonClick = async (): Promise<void> => {
    if (isTILATermsCheckboxChecked && requiresAutopayConfirmation) {
      showAutopayConfirmationModal()
    } else {
      setShowTILACheckboxErrorMessage(true)
    }
  }

  useEffect(() => {
    if (!requiresPayMonthlyTermsConsent) {
      dispatch(setConfirmTermsViewed(null))
    }
  }, [dispatch, requiresPayMonthlyTermsConsent])

  useEffect(() => {
    if (error?.extra !== undefined) {
      const { extra } = error
      if (extra.isEFAFinished) {
        dispatch(resetCheckoutError())
      }
    }
  }, [error])

  useEffect(() => {
    if (applePay.status === ApplePayStatus.Cancelled) {
      setClicked(false)
    }
  }, [applePay.status])

  // NOTE (CASHDAP-3070): PM keeps its bespoke summary routing rather than adopting the canonical
  // `SummaryRouteGuard`. The guard routes `IdentityIneligible` through `getIdentityVerificationRoute`,
  // which — with no identityResponse and no matching errorCode (PM's case) — falls through to
  // `Route.TERMINAL_ERROR`, whereas PM redirects to the verify flow. Full guard adoption is deferred
  // until PM's `IdentityIneligible` error payload is confirmed compatible.
  useEffect(() => {
    if (status <= CheckoutStatus.Loaded) {
      router.push(Route.LOGGED_IN)
    }

    if (status === CheckoutStatus.IdentityIneligible) {
      if (country === Country.GB) {
        router.push(Route.VERIFY_RETRY)
      } else {
        router.push(Route.VERIFY_IDENTITY)
      }
    }

    if (status === CheckoutStatus.LackOfEligiblePaymentMethod) {
      router.push(Route.ADD_PAYMENT_METHOD)
    }

    router.prefetch(Route.PAYMENT_METHOD)
  }, [country, router, status])

  useEffect(() => {
    if (error?.error !== 'undefined' && isCardScanRequiredErrorCode(error?.error as ErrorCode)) {
      return setClicked(false)
    } else if (typeof error?.error !== 'undefined' && Object.values(RecoverableErrorCode).includes(error?.error as RecoverableErrorCode)) {
      setClicked(false)
      setNotification({
        heading: t(`summary:error.${error?.error}.heading`),
        message: t(`summary:error.${error?.error}.message`),
        severe: true,
      })
    } else if (applePay.error !== undefined) {
      setClicked(false)
      if (applePay?.error === ApplePayError.InvalidCardType) {
        if (applePay?.cardType === PaymentSourceCaptureCardTypes.CREDIT) {
          setNotification({
            heading: t('summary:error.creditCardProhibitedForPaymentType.heading'),
            message: t('summary:error.creditCardProhibitedForPaymentType.message'),
            severe: false,
          })
        } else {
          setNotification({
            heading: t('summary:error.prepaidCard.heading'),
            message: t('summary:error.prepaidCard.message'),
            severe: false,
          })
        }
      } else if (applePay?.error === ApplePayError.BinBlocked) {
        setNotification({
          heading: t('summary:error.unsupportedCardIssuerBank.heading'),
          message: t('summary:error.unsupportedCardIssuerBank.message'),
          severe: false,
        })
      } else {
        setNotification({
          heading: t('summary:error.applePay.heading'),
          message: t('summary:error.applePay.message'),
          severe: true,
        })
      }
    }
  }, [error, applePay.error])

  // debit card should have higher priority than other payment methods.
  // other payment methods blocked errors will be displayed when no other debit cards on file.
  useEffect(() => {
    const switchToValidCard = selectedPaymentMethod?.type === PaymentMethodType.CREDIT_CARD && card !== null && card?.error === null
    if (preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT && switchToValidCard) {
      // need to show message that we've switched their payment method from ach to card.
      // card can be null due to the invocation of resetPreferredPaymentMethodErrors, so we don't want to show this error in that case.
      setNotification({
        heading: t(`consumerLending:bankAccountErrors.switchedToCard.heading`),
        message: t(`consumerLending:bankAccountErrors.switchedToCard.message`),
        severe: false,
      })
    } else if (
      preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT &&
      account !== null &&
      bankAccountReason !== null &&
      selectedPaymentMethod?.type === PaymentMethodType.ACH_BANK_ACCOUNT
    ) {
      if (!notAcceptedPaymentMethodEventSent) {
        logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: bankAccountReason })
        setnotAcceptedPaymentMethodEventSent(true)
      }

      setNotification({
        heading: t(`summary:bankAccountErrors.${bankAccountReason}.heading`),
        message: t(`summary:bankAccountErrors.${bankAccountReason}.message`),
        severe: !bankAccountValid,
      })
    } else if (preferredPaymentMethod === PaymentMethodType.APPLE_PAY && switchToValidCard && !applePayFeature.enabledForConsumerLending) {
      setNotification({
        heading: t(`consumerLending:applePayNotAllowed.heading_switched`),
        message: t(`consumerLending:applePayNotAllowed.message_switched`),
        severe: false,
      })
    } else if (
      preferredPaymentMethod === PaymentMethodType.APPLE_PAY &&
      selectedPaymentMethod?.type === PaymentMethodType.APPLE_PAY &&
      !applePayFeature.enabledForConsumerLending
    ) {
      setNotification({
        heading: t(`consumerLending:applePayNotAllowed.heading_cannotSwitch`),
        message: t(`consumerLending:applePayNotAllowed.message_cannotSwitch`),
        severe: true,
      })
    } else if (preferredPaymentMethod === PaymentMethodType.CASH_APP_PAY && switchToValidCard && !cashPayMonthlyEnabled) {
      setNotification({
        heading: t(`consumerLending:cashAppPayNotAllowed.heading_switched`),
        message: t(`consumerLending:cashAppPayNotAllowed.message_switched`),
        severe: false,
      })
    } else if (
      preferredPaymentMethod === PaymentMethodType.CASH_APP_PAY &&
      selectedPaymentMethod?.type === PaymentMethodType.CASH_APP_PAY &&
      !cashPayMonthlyEnabled
    ) {
      setNotification({
        heading: t(`consumerLending:cashAppPayNotAllowed.heading_cannotSwitch`),
        message: t(`consumerLending:cashAppPayNotAllowed.message_cannotSwitch`),
        severe: true,
      })
    } else if (cardExpiryLimitInMonths !== null && selectedPaymentMethod?.type === PaymentMethodType.CREDIT_CARD && reason === 'cardExpiryLimitInMonths') {
      const options = { context: switched ? 'switched' : 'cannotSwitch', count: cardExpiryLimitInMonths }

      if (!cardExpiryMonthsEventSent) {
        logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: NonTerminalErrors.ClCardExpiryError })
        setCardExpiryMonthsEventSent(true)
      }

      setNotification({
        heading: t(`summary:error.cardExpiryLimitInMonths.heading`, options),
        message: t(`summary:error.cardExpiryLimitInMonths.message`, options),
        severe: !valid,
      })
    } else if (card !== null && reason !== null) {
      if (!notAcceptedPaymentMethodEventSent) {
        logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: reason })
        setnotAcceptedPaymentMethodEventSent(true)
      }

      const options = { context: switched ? 'switched' : 'cannotSwitch' }
      setNotification({
        heading: t(`summary:error.${reason}.heading`, options),
        message: t(`summary:error.${reason}.message`, options),
        severe: !valid,
      })
    } else if (error == null && applePay.error === undefined) {
      // No preferred-payment-method / card error applies and there's no active checkout / Apple Pay
      // error (those are owned by the effects above). Clear any stale notification so a
      // previously-severe message (e.g. a blocked credit card) doesn't keep the CTA disabled after
      // the user selects a valid method via the inline accordion. The legacy flow navigated to the
      // payment-method page and back, remounting this page and resetting `notification`; the inline
      // accordion no longer remounts, so we clear explicitly here. Guarded so a recoverable checkout
      // or Apple Pay error isn't wiped on mount. (PR review — Builderbot P1 / Codex P2.)
      setNotification(undefined)
    }
  }, [
    card,
    valid,
    reason,
    switched,
    account,
    preferredPaymentMethod,
    bankAccountReason,
    selectedPaymentMethod,
    cardExpiryLimitInMonths,
    cashPayMonthlyEnabled,
    error,
    applePay.error,
  ])

  const renderAutopayToggle = (): ReactNode | null => {
    if (!requiresPayMonthlyAutopayControls) {
      return null
    }

    return <AutopayButton />
  }

  const renderCTAButton = (): ReactNode | null => {
    if (!requiresPayMonthlyTermsConsent || (isTILATermsCheckboxChecked && !requiresAutopayConfirmation)) {
      return (
        <TruthInLendingButton
          disabled={!ready || hasBlockingPaymentError || notification?.severe === true}
          isAutopayConfirmation={requiresPayMonthlyAutopayControls}
          loading={clicked}
          onLoadingChange={setClicked}
        />
      )
    }

    return (
      <Button
        padding='Fluid'
        loading={clicked}
        onClick={handleButtonClick}
        testNameSpace='consumer-lending-summary'
        disabled={!ready || clicked || notification?.severe}
      >
        {t('consumerLending:til.cta')}
      </Button>
    )
  }

  return (
    <>
      <NoErrorStatusGuard>
        <Head title={`${t('summary:reviewOrder')} | ${t('summary:brandName')}`} />
        <div className={styles.header}>
          <Heading size={headingSize} renderAs='h1'>
            {t('consumerLending:summary.title')}
          </Heading>
        </div>
        <div className={isDesktop ? redesignStyles.bodyWrapperDesktop : redesignStyles.bodyWrapperMobile}>
          <StartedStatusGuard>
            {isExpress && (
              <>
                <div className={styles.shippingSummary}>
                  {!isDeferredShipping && (
                    <div className={styles.shippingSummaryHeading}>
                      <Heading size={sectionHeadingSize} renderAs='h2'>
                        {isPickup ? t('common:orderCollectionDetails') : t('common:chooseShippingOptions')}
                      </Heading>
                    </div>
                  )}
                  <ShippingSummary hasBorder={false} />
                </div>
              </>
            )}
            {/* Cart preview: merchant name + item count (no order total) per the Figma SOT — the
              total moved into the Summary section. Behind an opt-in prop so other flows are
              unaffected. (CASHDAP-3316) */}
            <OrderSummary showItemCountHeader />
            {/* Payment section: plan schedule + change plan, then payment method, then automatic
              payments, grouped under one heading per the Figma SOT (frame 726:20432). PM keeps its
              dated timeline schedule rather than PI4's progress circles. */}
            <div className={redesignStyles.sectionHeading}>
              <Heading size={sectionHeadingSize} renderAs='h2'>
                {t('consumerLending:summary:paymentSectionTitle')}
              </Heading>
            </div>
            {/* "Change plan" sits inside the payment-schedule expandable, matching the Figma SOT
              (frame 726:20550) and PI4, where the change-schedule control lives within the schedule
              block rather than as a standalone button below it. `planSummaryHeader` renders the
              "{N} month plan" + "$X today, then $Y every month" header (CASHDAP-3315). */}
            <ConsumerLendingPaymentSchedule
              planSummaryHeader
              detailsFooter={
                <Button.Secondary
                  onClick={() => {
                    logEvent(TrackingEvent.PRESSED_CL_PLAN_EDIT_BUTTON)
                    showPaymentOptionsModal()
                  }}
                  padding='Fluid'
                  testNameSpace='consumer-lending-summary-redesign-change-plan'
                >
                  {t('consumerLending:summary:changePlan')}
                </Button.Secondary>
              }
            />
            {/* Payment method precedes "Automatic payments" per the Figma SOT (CASHDAP-3314). Inline
              accordion (tap to expand the card list, select to collapse, "Add payment method" to
              navigate) matching the US PI4 Arcade summary — `InlinePaymentMethod`. */}
            {/* The shared InlinePaymentMethod hard-codes aria-labelledby='select-payment-method' on
              its radio group (PaymentMethodListRedesigned); the standalone payment-method page
              renders that label element, but this inline summary flow does not. Provide a
              visually-hidden label so the radio group keeps an accessible name. (PR review —
              Codex P2.) */}
            <span id='select-payment-method' className={redesignStyles.srOnly}>
              {t('paymentMethod:chooseAPaymentMethod')}
            </span>
            {/* Preferred-card / bank / checkout / Apple Pay-runtime errors are shown by
              InlinePaymentMethod's own `SummaryNotification` (driven by the `SummaryNotificationProvider`
              that `page/summary/Summary.tsx` wraps this page in) — the same single inline source the US
              PI4 Arcade summary uses. We don't render a second copy of those here (that duplicated the
              error: one inline + one boxed). The gated block just below is only a fallback for the
              CL-specific cases SummaryNotification can't derive. (PR review — keep one error message,
              match PI4.) */}
            <InlinePaymentMethod />
            {/* Page-level notification fallback. Defers to InlinePaymentMethod's SummaryNotification
              whenever that component is actually showing something (context read above), so each notice
              renders exactly once. Covers everything the inline component stays silent on: its
              non-accordion branches (cash-only without extra methods, no saved method), bank
              notifications its clear-effect drops, and the CL-specific cases it can't derive
              (Apple Pay / Cash App Pay eligibility, ACH→card "switched"). Ensures a severe
              notification never leaves the CTA disabled with no visible message. */}
            {notification !== undefined && inlineNotification === undefined && (
              <div className={styles.notification}>
                {notification.severe ? (
                  <Text color='Fire' size={textSize}>
                    {notification?.message}
                  </Text>
                ) : (
                  <Message kind='Basic' iconName='Info' heading={notification.heading} description={notification?.message} />
                )}
              </div>
            )}
            {error !== undefined && <TrackOnMount eventName={TrackingEvent.VIEWED_NON_TERMINAL_ERROR} eventProperties={{ errorCode: error?.error }} />}
            {renderAutopayToggle()}
            <div className={redesignStyles.sectionDivider}>
              <Divider kind='Hairline' />
            </div>
            {/* Summary section (cost-of-credit breakdown — renders its own "Summary" heading). */}
            <ConsumerLendingSummaryBreakdown />
            <div className={redesignStyles.summaryActions}>
              {requiresPayMonthlyTermsConsent && (
                <Checkbox
                  id='tila-terms-checkbox'
                  error={showTILACheckboxErrorMessage}
                  aria-invalid={showTILACheckboxErrorMessage}
                  aria-describedby={showTILACheckboxErrorMessage ? TILA_TERMS_ERROR_ID : undefined}
                  label={<TILATerms setDocumentIdToShow={setDocumentIdToShow} />}
                  onChange={handleTILATermsCheckboxChange}
                />
              )}
              {renderTILACheckboxErrorMessage()}
              {renderCTAButton()}
            </div>
            {requiresPayMonthlyAutopayControls && <AutopayModal modalKey={AutopayConfirmationModalKey} autoPayAgreed={false} />}
            <TruthInLendingModal documentId={documentId} isTilDisclosure={tilDisclosure} />
            {clicked && <DisabledModal />}
          </StartedStatusGuard>
        </div>
      </NoErrorStatusGuard>
      {/* OUTSIDE NoErrorStatusGuard: inside it, the tearDownOnConfirm unmount (behind the
          processing modal) remounted this tracker when the modal closed, re-firing the page view
          AFTER confirm for ~42% of checkouts — inflating view counts and skewing per-view metrics. */}
      <TrackOnMount eventName={TrackingEvent.VIEWED_SUMMARY_PAGE} eventProperties={{ summaryPageVersion: PAY_MONTHLY_SUMMARY_VERSION_REDESIGN }} />
    </>
  )
}
